import { useConfirmationPage } from "../../../hooks/use-confirmation-page";
import { useRecommendedTemplate } from "../../../hooks/use-recommended-template";
import {
    queryParam,
    useRegisterMetadata
} from "../../../hooks/use-registered-metadata";
import {
    BLANK_TEMPLATE_TEAM_MANAGED_KEY,
    CAMPAIGN_MANAGEMENT_KEY,
    CONTENT_MANAGEMENT_KEY,
    DESIGN_WEB_DESIGN_PROCESS_KEY,
    FINANCE_BUDGET_CREATION_KEY,
    HUMAN_RESOURCES_NEW_EMPLOYEE_ONBOARDING_KEY,
    HUMAN_RESOURCES_RECRUITMENT_KEY,
    KANBAN_TEAM_MANAGED_KEY,
    LEGAL_IP_INFRINGEMENT_KEY,
    PROCESS_CONTROL_KEY,
    PROCUREMENT_KEY,
    PROJECT_MANAGEMENT_KEY,
    SALES_LEAD_TRACKING_KEY,
    SALES_SALES_PIPELINE_KEY
} from "../../../models/percieved-provisioning";
import {
    LANDING_GATE_NON_TECH_TEAM_TYPES,
    USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
    USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE,
    USER_SEGMENTATION_TEAM_TYPE_DESIGN,
    USER_SEGMENTATION_TEAM_TYPE_FINANCE,
    USER_SEGMENTATION_TEAM_TYPE_HR,
    USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
    USER_SEGMENTATION_TEAM_TYPE_LEGAL,
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
    USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
    USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_SALES,
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE
} from "../../../models/user-segmentation";
import { getJtbdTemplateOverrideQueryParam } from "../../../utils/utils";
import {
    defaultWizardConfiguration,
    KANBAN_PROJECT_DEFAULTS,
    SCRUM_PROJECT_DEFAULTS,
    USER_SEGMENTATION_TEAM_TYPE_UNKNOWN,
    wizardConfigurations,
    WizardTemplateOption
} from "../utils/types";

// eslint-disable-next-line complexity
export const useWizardConfiguration = () => {
    const {
        userSegmentationContext: { selectedTeamType = "" }
    } = useConfirmationPage();
    const metadata = useRegisterMetadata();
    const templateChoice =
        metadata?.get(queryParam.templateChoice) ??
        getJtbdTemplateOverrideQueryParam();
    const templateId =
        metadata?.get(queryParam.templateId) ??
        getJtbdTemplateOverrideQueryParam();
    const teamType = selectedTeamType ?? USER_SEGMENTATION_TEAM_TYPE_UNKNOWN;

    let projectDetails = SCRUM_PROJECT_DEFAULTS;

    if (
        templateChoice === "kanban" ||
        templateId
            ?.toString()
            .startsWith("com.atlassian.jira-core-project-templates:")
    ) {
        projectDetails = KANBAN_PROJECT_DEFAULTS;
    }

    return {
        teamType,
        wizardConfiguration:
            wizardConfigurations[teamType] || defaultWizardConfiguration,
        projectDetails
    };
};

export const useShowOnboardingWizardScreen = () => {
    const metadata = useRegisterMetadata();
    const {
        userSegmentationContext: { selectedTeamType = "" }
    } = useConfirmationPage();

    const templateChoice =
        metadata?.get(queryParam.templateChoice) ??
        getJtbdTemplateOverrideQueryParam();

    // Targeting Check: Non-software team types who choose Kanban and are enrolled in the experiment
    if (
        selectedTeamType === USER_SEGMENTATION_TEAM_TYPE_SOFTWARE ||
        templateChoice !== "kanban"
    ) {
        return { userEligibleForWizardExperiment: false };
    }

    return {
        // we only want to show the screen if the user is in the variant
        // we skip the screen if the user is in the control group
        userEligibleForWizardExperiment: true
    };
};

export const useShowOnboardingWizardScreenForScrum = () => {
    const metadata = useRegisterMetadata();

    const templateChoice =
        metadata?.get(queryParam.templateChoice) ??
        getJtbdTemplateOverrideQueryParam();

    if (templateChoice === "scrum") {
        return { userEligibleForWizardForScrumExperiment: true };
    }

    return {
        userEligibleForWizardForScrumExperiment: false
    };
};

export const useShowOnboardingWizardScreenForKanban = () => {
    const metadata = useRegisterMetadata();

    const templateChoice =
        metadata?.get(queryParam.templateChoice) ??
        getJtbdTemplateOverrideQueryParam();

    if (templateChoice === "kanban") {
        return { userEligibleForWizardForKanban: true };
    }

    return { userEligibleForWizardForKanban: false };
};

export const useShowOnboardingWizardScreenForRouteBiz = () => {
    const metadata = useRegisterMetadata();

    const templateId =
        metadata?.get(queryParam.templateId) ??
        getJtbdTemplateOverrideQueryParam();

    if (
        templateId
            ?.toString()
            .startsWith("com.atlassian.jira-core-project-templates:")
    ) {
        return { userEligibleForWizardForRouteBizExperiment: true };
    }

    return { userEligibleForWizardForRouteBizExperiment: false };
};

export const useEligibleForSampleProject = () => {
    const templateId = useRecommendedTemplate();
    const {
        userSegmentationContext: { selectedTeamType = "" }
    } = useConfirmationPage();

    const enrollmentPreconditionsMet =
        [
            KANBAN_TEAM_MANAGED_KEY,
            BLANK_TEMPLATE_TEAM_MANAGED_KEY,
            CAMPAIGN_MANAGEMENT_KEY,
            CONTENT_MANAGEMENT_KEY,
            PROCESS_CONTROL_KEY,
            PROJECT_MANAGEMENT_KEY,
            PROCUREMENT_KEY,
            DESIGN_WEB_DESIGN_PROCESS_KEY,
            HUMAN_RESOURCES_NEW_EMPLOYEE_ONBOARDING_KEY,
            HUMAN_RESOURCES_RECRUITMENT_KEY,
            LEGAL_IP_INFRINGEMENT_KEY,
            FINANCE_BUDGET_CREATION_KEY,
            SALES_SALES_PIPELINE_KEY,
            SALES_LEAD_TRACKING_KEY
        ].includes(templateId) &&
        [
            USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
            USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE,
            USER_SEGMENTATION_TEAM_TYPE_DESIGN,
            USER_SEGMENTATION_TEAM_TYPE_FINANCE,
            USER_SEGMENTATION_TEAM_TYPE_HR,
            USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
            USER_SEGMENTATION_TEAM_TYPE_LEGAL,
            USER_SEGMENTATION_TEAM_TYPE_MARKETING,
            USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
            USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
            USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
            USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
            USER_SEGMENTATION_TEAM_TYPE_SALES,
            USER_SEGMENTATION_TEAM_TYPE_SOFTWARE
        ].includes(selectedTeamType);

    if (enrollmentPreconditionsMet) {
        return { userEligibleForSampleProject: true };
    }

    return { userEligibleForSampleProject: false };
};

export const useEligibleForSimplifiedLandingViewWithBasicTasks = (
    template?: WizardTemplateOption
) => {
    const {
        userSegmentationContext: { selectedTeamType = "" }
    } = useConfirmationPage();

    if (
        selectedTeamType === USER_SEGMENTATION_TEAM_TYPE_SOFTWARE ||
        template !== "KANBAN"
    ) {
        return { userEligibleForSimplifiedLandingView: false };
    } else {
        return { userEligibleForSimplifiedLandingView: true };
    }
};

export const useEligibleForBasicSampleTasksForScrumOrSoftwareKanban = () => {
    const metadata = useRegisterMetadata();

    const {
        userSegmentationContext: { selectedTeamType = "" }
    } = useConfirmationPage();

    const templateChoice =
        metadata?.get(queryParam.templateChoice) ??
        getJtbdTemplateOverrideQueryParam();

    const isScrum = templateChoice === "scrum";
    const isSoftwareKanban =
        selectedTeamType === USER_SEGMENTATION_TEAM_TYPE_SOFTWARE &&
        templateChoice === "kanban";

    if (isScrum || isSoftwareKanban) {
        return {
            userEligibleForBasicSampleTasksForScrumOrSoftwareKanban: true
        };
    }

    return { userEligibleForBasicSampleTasksForScrumOrSoftwareKanban: false };
};

export const useEligibleForSampleTasksForLandingGateNonTech = () => {
    const {
        userSegmentationContext: { selectedTeamType = "" }
    } = useConfirmationPage();

    if (
        LANDING_GATE_NON_TECH_TEAM_TYPES.includes(
            selectedTeamType as (typeof LANDING_GATE_NON_TECH_TEAM_TYPES)[number]
        )
    ) {
        return { userEligibleForSampleTasksForLandingGateNonTech: true };
    }

    return { userEligibleForSampleTasksForLandingGateNonTech: false };
};
